import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { X, Lock, Loader2, Zap, FileText, Check } from 'lucide-react';
import { useAuth } from '@clerk/react';
import { platform } from '@/platform.config';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

interface BillingWallProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function BillingWall({ projectId, onClose, onSuccess }: BillingWallProps) {
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getToken } = useAuth();

  // Fetch live prices on mount
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const baseUrl = platform.hubUrl;
        const response = await fetch(`${baseUrl}/api/billing/v1/products`);
        if (response.ok) {
          const data = await response.json();
          if (data.prices) setPrices(data.prices);
        }
      } catch (e) {
        console.error("Failed to fetch live prices", e);
      }
    };
    fetchPrices();
  }, []);

  useEffect(() => {
    if (!selectedProduct) return;
    
    const fetchSession = async () => {
      setLoading(true);
      setError(null);
      try {
        const baseUrl = platform.hubUrl;
        
        const token = await getToken();
        const response = await fetch(`${baseUrl}/api/billing/v1/create-session`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            projectId,
            productId: selectedProduct
          }),
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to create checkout session');
        
        setClientSecret(data.clientSecret);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [projectId, selectedProduct]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop with extreme blur */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-3xl" onClick={onClose} />

      <div className="relative w-full max-w-4xl h-[90vh] bg-white rounded-[2.5rem] shadow-[0_50px_100px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col border border-white/20">
        <header className="px-10 py-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-5">
            <div className="bg-blue-600 text-white p-3 rounded-2xl shadow-lg">
              <Lock size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-950 uppercase tracking-tight">Report Authorization</h2>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Unlock professional PDF generation</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-3 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-2xl transition-all"
          >
            <X size={24} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-1 text-center">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-6">
              <Loader2 className="animate-spin text-blue-600" size={48} />
              <p className="font-black text-xs uppercase tracking-[0.3em] text-slate-400">Initializing Stripe Secure Checkout...</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center gap-6 p-10">
              <div className="bg-red-50 text-red-500 p-6 rounded-3xl border border-red-100">
                <p className="font-bold">{error}</p>
              </div>
              <button 
                onClick={() => { setSelectedProduct(null); setError(null); }}
                className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest"
              >
                Go Back
              </button>
            </div>
          ) : clientSecret ? (
            <div className="min-h-full bg-white">
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-3xl">
                {/* Single Report Card */}
                <button
                  onClick={() => setSelectedProduct('tradesketch_single_report')}
                  className="group relative flex flex-col p-8 bg-white border-2 border-slate-100 rounded-[2rem] hover:border-blue-600 hover:shadow-2xl hover:shadow-blue-600/10 transition-all text-left overflow-hidden active:scale-[0.98]"
                >
                  <div className="w-14 h-14 bg-slate-100 group-hover:bg-blue-600 text-slate-400 group-hover:text-white rounded-2xl flex items-center justify-center mb-6 transition-all">
                    <FileText size={28} />
                  </div>
                  <h3 className="text-xl font-black text-slate-900 uppercase">Single Report</h3>
                  <p className="text-sm font-bold text-slate-400 mt-2 mb-8 leading-relaxed">
                    Pay once for this background. Standard PDF generation for this project only.
                  </p>
                  <div className="mt-auto pt-6 border-t border-slate-50 flex items-center justify-between">
                    <span className="text-2xl font-black text-slate-950">
                      {prices['tradesketch_single_report'] || "Select Plan"}
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-600">Buy Now</span>
                  </div>
                </button>

                {/* Pro Subscription Card */}
                <button
                  onClick={() => setSelectedProduct('tradesketch_pro_sub')}
                  className="group relative flex flex-col p-8 bg-slate-950 border-2 border-slate-950 rounded-[2rem] hover:shadow-2xl hover:shadow-blue-600/20 transition-all text-left overflow-hidden active:scale-[0.98]"
                >
                  <div className="absolute top-0 right-0 bg-blue-600 text-white px-6 py-2 rounded-bl-3xl font-black text-[9px] uppercase tracking-[0.2em] shadow-xl">
                    Best Value
                  </div>
                  <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center mb-6 transition-all shadow-lg shadow-blue-600/20">
                    <Zap size={28} />
                  </div>
                  <h3 className="text-xl font-black text-white uppercase">Pro Subscription</h3>
                  <p className="text-sm font-bold text-slate-400 mt-2 mb-8 leading-relaxed">
                    Unlimited reports for all projects. Priority cloud processing and professional branding.
                  </p>
                  <div className="mt-auto pt-6 border-t border-white/5 flex items-center justify-between">
                    <span className="text-2xl font-black text-white">
                      {prices['tradesketch_pro_sub'] || "Upgrade Now"}
                    </span>
                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-500">Enable Pro</span>
                  </div>
                </button>
              </div>

              <div className="mt-12 flex items-center gap-2 px-6 py-3 bg-slate-50 rounded-2xl border border-slate-100">
                <Check size={14} className="text-blue-600" />
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Instant unlock upon successful payment</p>
              </div>
            </div>
          )}
        </div>

        <footer className="px-10 py-6 border-t border-slate-100 text-center bg-slate-50/30">
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.4em]">Secure Payment powered by Stripe • No data stored on local servers</p>
        </footer>
      </div>
    </div>
  );
}
